import React from "react";
import { Box, Typography } from "@mui/material";
import mappedIcon from "assets/images/mappedicon.svg";
import {
  auditDashboardTheme,
  auditToneStyles,
  getAuditSummaryComparisonStyles,
  type AuditSummaryComparisonVariant,
} from "../../theme";
import type { AuditMetricRowData } from "../../types/auditMetrics";

interface AuditSummaryComparisonTableProps {
  rows: AuditMetricRowData[];
  variant: AuditSummaryComparisonVariant;
}

const columnHeaders = ["Expected", "POS Reported", "Variance"] as const;

const isZeroVariance = (value: React.ReactNode) => value === 0 || value === "0";

type AuditSummaryComparisonStyles = ReturnType<typeof getAuditSummaryComparisonStyles>;

const renderColumnHeader = (label: (typeof columnHeaders)[number], splitColumnLabels: boolean) => {
  if (!splitColumnLabels || label === "Variance") {
    return label;
  }

  const [firstLine, secondLine] = label.split(" ");
  return (
    <>
      {firstLine}
      <br />
      {secondLine}
    </>
  );
};

function AuditSummaryVarianceCell({
  row,
  styles,
}: {
  row: AuditMetricRowData;
  styles: AuditSummaryComparisonStyles;
}) {
  const toneStyles = auditToneStyles[row.tone ?? "matched"];
  const showMappedIcon = row.variance == null || row.variance === "" || isZeroVariance(row.variance);

  return (
    <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", minWidth: 0 }}>
      <Typography
        component="span"
        sx={{
          ...styles.varianceCell,
          color: toneStyles.text,
        }}
      >
        {showMappedIcon ? (
          <Box
            component="img"
            src={mappedIcon}
            alt="matched"
            sx={{
              width: styles.matchedIconSize,
              height: styles.matchedIconSize,
              display: "block",
            }}
          />
        ) : (
          row.variance
        )}
      </Typography>
    </Box>
  );
}

function AuditSummaryRowHeader({
  row,
  styles,
}: {
  row: AuditMetricRowData;
  styles: AuditSummaryComparisonStyles;
}) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography component="div" sx={styles.rowHeader}>
        {row.label}
      </Typography>
      {styles.showHelperText && row.helperText ? (
        <Typography component="div" sx={styles.helperText}>
          {row.helperText}
        </Typography>
      ) : null}
    </Box>
  );
}

function AuditSummaryValueCell({
  value,
  styles,
}: {
  value: React.ReactNode;
  styles: AuditSummaryComparisonStyles;
}) {
  return <Typography sx={styles.valueCell}>{value ?? ""}</Typography>;
}

function AuditSummaryMetricRow({
  row,
  styles,
}: {
  row: AuditMetricRowData;
  styles: AuditSummaryComparisonStyles;
}) {
  return (
    <Box sx={styles.row}>
      <AuditSummaryRowHeader row={row} styles={styles} />
      <AuditSummaryValueCell value={row.expected} styles={styles} />
      <AuditSummaryValueCell value={row.reported} styles={styles} />
      <AuditSummaryVarianceCell row={row} styles={styles} />
    </Box>
  );
}

function AuditSummaryDetailNotes({
  notes,
  styles,
}: {
  notes: string[];
  styles: AuditSummaryComparisonStyles;
}) {
  return (
    <Box sx={styles.detailNotes}>
      {notes.map((note) => (
        <Typography key={note} component="div" sx={styles.detailNoteText}>
          {note}
        </Typography>
      ))}
    </Box>
  );
}

function AuditSummaryTrailingNote({
  note,
  styles,
}: {
  note: React.ReactNode;
  styles: AuditSummaryComparisonStyles;
}) {
  const noteText = typeof note === "string" ? note : "";
  const dividerIndex = noteText.indexOf(":");
  const noteLabel = dividerIndex > -1 ? noteText.slice(0, dividerIndex + 1) : "";
  const noteValue = dividerIndex > -1 ? noteText.slice(dividerIndex + 1).trimStart() : noteText;

  return (
    <Box sx={styles.trailingNote}>
      <Typography component="div" sx={styles.trailingNoteText}>
        {noteLabel ? (
          <>
            <Box component="strong" sx={styles.trailingNoteLabel}>
              {noteLabel}
            </Box>{" "}
            <Box component="span">{noteValue}</Box>
          </>
        ) : (
          note
        )}
      </Typography>
    </Box>
  );
}

export default function AuditSummaryComparisonTable({ rows, variant }: AuditSummaryComparisonTableProps) {
  const styles = getAuditSummaryComparisonStyles(variant);

  return (
    <Box sx={{ minWidth: 0 }}>
      <Box sx={styles.header}>
        <Box />
        {columnHeaders.map((label) => (
          <Typography
            key={label}
            sx={{
              ...styles.columnHeader,
              whiteSpace: label === "Variance" ? "nowrap" : styles.columnHeader.whiteSpace,
            }}
          >
            {renderColumnHeader(label, styles.splitColumnLabels)}
          </Typography>
        ))}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        {rows.map((row, index) => {
          const previousRow = rows[index - 1];
          const previousRowHasStrongDivider = previousRow?.dividerAfter === "strong";
          const previousRowHasTrailingNote = Boolean(previousRow?.trailingNote);
          const showTopDivider = styles.showFirstRowDivider
            ? index === 0 || (previousRowHasStrongDivider && !previousRowHasTrailingNote)
            : index > 0 && previousRowHasStrongDivider;
          const borderColor = showTopDivider
            ? auditDashboardTheme.colors.lineStrong
            : row.dividerAfter === "strong"
              ? auditDashboardTheme.colors.lineStrong
              : auditDashboardTheme.colors.lineSoft;

          return (
            <Box key={row.id}>
              <Box
                sx={{
                  ...styles.rowContainer,
                  pt: variant === "range" ? (showTopDivider ? "4px" : 0) : "4px",
                  borderTop: showTopDivider ? "1px solid" : "none",
                  borderColor,
                }}
              >
                <AuditSummaryMetricRow row={row} styles={styles} />
              </Box>

              {styles.showDetailNotes && row.detailNotes?.length ? (
                <AuditSummaryDetailNotes notes={row.detailNotes} styles={styles} />
              ) : null}

              {styles.showTrailingNote && row.trailingNote ? (
                <AuditSummaryTrailingNote note={row.trailingNote} styles={styles} />
              ) : null}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
